<template>
  <ai-carousel-with-sections
    id="HotelRooms"
    access-id="HotelRoomsCarousel"
    :items="items"
    mobile-size="4"
    tabs-variant="tab-link-01"
    mobile-button
    :sections="sections"
    :title="$t('hotel.room.title')"
    :gutter="gutter"
    arrows-top-position="10%"
    hide-gallery
    :slide-width="slideWidth"
    tabs-size="large"
    :aria-label="$t('accessibility.description.hotelRoomsCarousel')" />
</template>

<script setup lang="ts">
import { differenceInCalendarDays } from 'date-fns';

import { formatDate } from '~/helpers/date.helper';

import { RoomCategory, RoomCategoryOrder } from '../../constants';
import { getSortedRoomAmenities } from '../../helpers';

import type { UnconfiguredGuests } from '~~/domains/booking';
import { useBookingUrlGenerator } from '~~/domains/booking';
import type { Accommodation, AvailabilityRoom } from '~~/domains/graphql';
import type { AiCarouselItem } from '~~/domains/ux/molecules/AiCarousel/types';
import AiRoomCard from '~~/domains/ux/molecules/AiRoomCard/AiRoomCard.vue';

export interface AiCarouselItemWithSection extends AiCarouselItem {
  sectionId: string;
}

interface Props {
  ridcode?: string;
  rooms?: Accommodation[];
  availabilities?: AvailabilityRoom[];
  roomsAndGuests: UnconfiguredGuests[];
  dates: Date[];
}

const props = defineProps<Props>();

const localePath = useLocalePath();
const isSmallDevice = useMatchMediaQuery('tablet');
const isDesktopDevice = useMatchMediaQuery('large-desktop');

const gutter = computed(() => {
  if (isSmallDevice.value) return 16;

  return isDesktopDevice.value ? 64 : 32;
});
const slideWidth = computed(() => (isSmallDevice.value ? 0.9 : 0.5));

const items = computed<AiCarouselItemWithSection[]>(() => {
  if (!Array.isArray(props.rooms)) return [];

  return props.rooms
    .map<AiCarouselItemWithSection>(room => {
      const isAvailable = isRoomAvailable(room, props.availabilities);
      const bookingUrl = generateBookingUrl(room, isAvailable);

      return {
        component: AiRoomCard,
        key: `hotel-room-${room.id as string}`,
        props: {
          amenities: getSortedRoomAmenities(room, t),
          isAvailable,
          bookingUrl,
          image: room.medias?.photos?.[0] ?? undefined,
          room,
          section: room.classification?.type,
          title: room.label,
          useRouteQuery: true,
        },
        sectionId: room.classification?.type as string,
      };
    })
    .filter(Boolean)
    .sort((a, b) => {
      if (a.sectionId < b.sectionId) {
        return -1;
      }
      if (a.sectionId > b.sectionId) {
        return 1;
      }
      return 0;
    });
});

const isRoomAvailable = (
  room: Accommodation,
  availabilities?: AvailabilityRoom[],
): boolean => {
  if (!Array.isArray(availabilities)) return false;

  const roomAvailability = availabilities.find(
    availability => availability.code === room.id,
  );

  return (roomAvailability?.offers?.length || 0) > 0;
};

const { buildQueryParameters } = useBookingUrlGenerator();

const generateBookingUrl = (
  room: Accommodation,
  isAvailable: boolean,
): string | undefined => {
  if (typeof isAvailable !== 'boolean') return;

  const query = buildQueryParameters(
    roomsAndGuests.value,
    props.ridcode as string,
    dateIn.value,
    Math.abs(nights.value),
  );

  if (!isAvailable) {
    return localePath({
      name: 'booking-ridcode-room',
      params: { ridcode: props.ridcode as string },
      query,
    });
  } else {
    return localePath({
      name: 'booking-ridcode-rate',
      params: { ridcode: props.ridcode as string },
      query: {
        ...query,
        'current-productCode': room.id,
      },
    });
  }
};

const { t } = useI18n();

const sections = computed(() => {
  if (!Array.isArray(items.value)) return [];

  const itemCategories = items.value.reduce<
    { label: string; id: RoomCategory }[]
  >((acc, item) => {
    if (
      acc.some(itm => itm.id === (item.sectionId as RoomCategory)) ||
      !RoomCategoryOrder.includes(item.sectionId as RoomCategory)
    )
      return acc;

    acc.push({
      id: item.sectionId as RoomCategory,
      label: t(`hotel.room.category.${item.sectionId}`),
    });

    return acc;
  }, []);

  const categoryPonderation = {
    [RoomCategory.Room]: RoomCategoryOrder.indexOf(RoomCategory.Room),
    [RoomCategory.Suite]: RoomCategoryOrder.indexOf(RoomCategory.Suite),
    [RoomCategory.Villa]: RoomCategoryOrder.indexOf(RoomCategory.Villa),
    [RoomCategory.Penthouse]: RoomCategoryOrder.indexOf(RoomCategory.Penthouse),
  };

  return itemCategories.sort(
    (ic1, ic2) => categoryPonderation[ic1.id] - categoryPonderation[ic2.id],
  );
});

// eslint-disable-next-line vue/no-dupe-keys
const roomsAndGuests = ref<UnconfiguredGuests[]>([]);
const nights = ref<number>(0);
const dateIn = ref<string>('');

onMounted(() => {
  roomsAndGuests.value = props.roomsAndGuests.length
    ? props.roomsAndGuests
    : [{ adults: 2, childrenAges: [] }];
  nights.value = differenceInCalendarDays(props.dates[0], props.dates[1]);
  dateIn.value = formatDate(props.dates[0], 'yyyy-MM-dd');
});
</script>
